// The URL to your endpoint
const baseURL = "https://elfly-dot-erudite-imprint-406809.ey.r.appspot.com";

let _cachedToken = null;
let _tokenExpiry = 0;

/**
 * Retrieves and caches a Clerk session token for authenticated server requests.
 *
 * This function minimizes unnecessary token requests by caching the token in memory
 * and returning it until shortly before its expiration. It automatically refreshes
 * the token when it is close to expiry (within 60 seconds).
 *
 * @async
 * @function getCachedToken
 * @returns {Promise<string>} A valid JWT token string for server authentication.
 *
 * @description
 * - Uses `window.Clerk.session.getToken({ template: "server" })` to request a new token.
 * - Decodes the token payload to determine its expiry (`exp`) and stores both the token
 *   and expiry timestamp in local variables (`_cachedToken`, `_tokenExpiry`).
 * - If a valid cached token exists and has more than 60 seconds remaining, it is returned
 *   immediately without re-fetching.
 *
 * @throws {Error} Throws if Clerk is not initialized or if fetching the token fails.
 *
 * @example
 * const token = await getCachedToken();
 * console.log("Server token:", token);
 *
 * @example
 * // Used internally by fetchServerData:
 * const data = await fetchServerData('/api/secure-route');
 */
async function getCachedToken() {
  const now = Date.now();
  if (_cachedToken && now < _tokenExpiry - 60_000) return _cachedToken;

  // IMPORTANT: use a named server template
  const token = await window.Clerk.session.getToken({ template: "server" });

  const [, payloadBase64] = token.split(".");
  const payloadJson = atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"));
  const { exp } = JSON.parse(payloadJson);
  _tokenExpiry = exp * 1000;
  _cachedToken = token;
  return token;
}

/**
 * Fetches data from the server using the provided endpoint and options.
 *
 * This is a general-purpose wrapper around the Fetch API designed for requests
 * to a single server. It automatically attaches authentication headers using a cached token
 * (via `getCachedToken()`), sets default headers, and merges any custom request options.
 *
 * @async
 * @function fetchServerData
 * @param {string} endpoint - The API endpoint to fetch from. Can include or omit the leading slash (`/`).
 * @param {Object} [options={}] - Optional fetch configuration that overrides or extends defaults.
 * @param {string} [options.method='GET'] - HTTP method (e.g., `"GET"`, `"POST"`, `"PUT"`, `"DELETE"`).
 * @param {Object|string} [options.body] - The request body, typically a JSON stringified object for non-GET requests.
 * @param {Object} [options.headers] - Additional headers to merge with the default headers.
 * @param {RequestCredentials} [options.credentials='include'] - Controls whether to send cookies with the request.
 *
 * @returns {Promise<any>} The parsed JSON response from the server.
 *
 * @throws {Error} Throws an error if the network request fails or if the server response is not OK (status not in the 200–299 range).
 *
 * @example
 * // Example: Simple GET request
 * const data = await fetchServerData('/api/users');
 *
 * @example
 * // Example: POST request with custom body and headers
 * const result = await fetchServerData('/api/users', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'Web', role: 'Developer' }),
 *   headers: { 'X-Custom-Header': 'CustomValue' },
 * });
 *
 * @example
 * // Example: DELETE request
 * await fetchServerData('/api/users/123', { method: 'DELETE' });
 */
export async function fetchServerData(endpoint, options = {}) {
  try {
    const token = await getCachedToken();
    const url =
      baseURL + (endpoint.startsWith("/") ? endpoint : "/" + endpoint);

    const defaultOptions = {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    const mergedOptions = { ...defaultOptions, ...options };
    const response = await fetch(url, mergedOptions);
    if (!response.ok) {
      console.error("Response details:", {
        status: response.status,
        url,
        endpoint,
      });
      throw new Error(`Network response was not ok: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Fetch error details:", {
      message: error.message,
      endpoint,
      fullUrl: baseURL + endpoint,
    });
    throw error;
  }
}
